import React, {useMemo} from 'react'
import {Text, TextProps} from '@chakra-ui/react'
import {sanitizePageDesignerHtml} from '_utils'

type RichTextProps = Omit<TextProps, 'children' | 'content' | 'dangerouslySetInnerHTML'> & {
    /** Raw text/HTML coming from a Page Designer field (may contain escaped inline tags like `<br>`). */
    content?: string | null
}

/**
 * RichText renders inline-formatted text coming from Page Designer fields.
 *
 * Business context: authors need to embed light inline HTML (line breaks, emphasis) directly
 * in Page Designer text fields to fine-tune page rendering, without a dedicated customization
 * attribute per case. Page Designer stores that markup escaped (e.g. `&lt;br&gt;`), so the
 * SCAPI getPage response hands back a literal `<br>` string. Rendered as plain text it shows
 * verbatim on the page; RichText sanitizes the value (whitelisted inline tags only) and renders
 * it as HTML so the authored formatting takes effect, consistently across every PD module.
 *
 * It is a thin wrapper over Chakra's `Text`, so it accepts the same style props the plain
 * `<Text>` it replaces was already receiving.
 *
 * SECURITY: This is the single, intentional place where authored HTML is injected. React's
 * `dangerouslySetInnerHTML` is the only API to render an HTML string, and its name flags the
 * XSS surface it opens. That risk is contained here because (1) the source is trusted editorial
 * copy from Page Designer, not end-user input, and (2) every value passes through
 * `sanitizePageDesignerHtml`, which strips non-whitelisted tags and unsafe URLs. Keep those two
 * invariants: do not use RichText for untrusted input, and do not bypass the sanitizer.
 */
const RichText: React.FC<RichTextProps> = ({content, ...textProps}) => {
    const html = useMemo(() => sanitizePageDesignerHtml(content), [content])

    if (!html) return null

    // dangerouslySetInnerHTML is React's only HTML-injection API; safe here — see SECURITY note above.
    return <Text {...textProps} dangerouslySetInnerHTML={{__html: html}} />
}

export {RichText}
export type {RichTextProps}
